import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FileText, Download, Search, Circle, Plus, Sparkles, X, Pencil, Trash2, Mail, Send, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import BackButton from '../../../components/admin/BackButton';
import { useToast } from '../../../hooks/use-toast';
import GeneratePDFWizard from '../cms/GeneratePDFWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700 border border-gray-300',
  sent: 'bg-blue-100 text-blue-800 border border-blue-300',
  signed: 'bg-green-100 text-green-800 border border-green-300',
  archived: 'bg-gray-200 text-gray-600 border border-gray-300',
  voided: 'bg-red-100 text-red-700 border border-red-300',
};

const TYPE_COLORS = {
  loi: 'bg-[#023E8A] text-white',
  nda: 'bg-[#FF6B35] text-white',
  ncnda: 'bg-[#DC2626] text-white',
  confidentiality: 'bg-[#00B4D8] text-white',
  cover_letter: 'bg-[#6C757D] text-white',
  partnership: 'bg-[#198754] text-white',
  spa: 'bg-[#8B5CF6] text-white',
  mou: 'bg-[#0F766E] text-white',
  custom: 'bg-gray-800 text-white',
};

const GeneratedDocumentsPage = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [picker, setPicker] = useState({ open: false });
  const [emailDialog, setEmailDialog] = useState(null);
  const [wizard, setWizard] = useState({ open: false, template: null, editingDoc: null });
  const { toast } = useToast();

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/crm/generated-documents/?limit=500`);
      setDocs(r.data || []);
    } catch (_) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      if (statusFilter && d.status !== statusFilter) return false;
      if (typeFilter && d.template_type !== typeFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return [d.template_name, d.template_code, d.filename, d.counterparty_snapshot?.company_name]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(s));
    });
  }, [docs, search, statusFilter, typeFilter]);

  const updateStatus = async (doc, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/crm/generated-documents/${doc.id}`, { status: newStatus });
      toast({ title: 'Updated', description: `Status → ${newStatus}` });
      fetchDocs();
    } catch (_) { /* ignore */ }
  };

  const handleEditDraft = async (doc) => {
    // Fetch full template to re-open the wizard
    try {
      const r = await axios.get(`${API_URL}/api/cms/document-templates/${doc.template_id}`);
      if (!r.data) throw new Error('Template not found');
      setWizard({ open: true, template: r.data, editingDoc: doc });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Cannot edit draft',
        description: e.response?.data?.detail || 'The original template was deleted or is unavailable.',
      });
    }
  };

  const handleDelete = async (doc) => {
    const label = doc.template_name || doc.template_code || 'this document';
    const counterparty = doc.counterparty_snapshot?.company_name || doc.counterparty_snapshot?.name || '—';
    if (!window.confirm(
      `Delete "${label}" (${counterparty})?\n\n` +
      `This permanently removes the audit-trail record. The original template is NOT affected.\n\n` +
      `This action cannot be undone.`
    )) return;
    try {
      await axios.delete(`${API_URL}/api/crm/generated-documents/${doc.id}`);
      toast({ title: 'Deleted', description: `${label} removed from audit trail` });
      setDocs((prev) => prev.filter((x) => x.id !== doc.id));
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: e.response?.data?.detail || 'Could not delete document.',
      });
    }
  };

  const counts = useMemo(() => {
    const c = { total: docs.length, draft: 0, sent: 0, signed: 0, archived: 0, voided: 0 };
    docs.forEach((d) => { c[d.status] = (c[d.status] || 0) + 1; });
    return c;
  }, [docs]);

  return (
    <div className="p-6 space-y-5" data-testid="generated-documents-page">
      <BackButton />
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#023E8A]" /> CRM Documents
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Audit trail of every document generated via the Document Templates CMS — tracked per issuer company and counterparty.
          </p>
        </div>
        <Button
          onClick={() => setPicker({ open: true })}
          className="bg-[#FF6B35] hover:bg-[#E5562D] text-white"
          data-testid="crm-docs-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Document
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {['total', 'draft', 'sent', 'signed', 'archived', 'voided'].map((k) => (
          <div key={k} className="bg-white border rounded-lg px-3 py-2">
            <div className="text-xs text-[#6C757D] capitalize">{k}</div>
            <div className="text-lg font-bold text-[#2B2D42]">{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            placeholder="Search by template, counterparty, filename…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="crm-docs-search"
          />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="crm-docs-type-filter">
          <option value="">All types</option>
          {Object.keys(TYPE_COLORS).map((t) => (<option key={t} value={t}>{t.toUpperCase()}</option>))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="crm-docs-status-filter">
          <option value="">All statuses</option>
          {['draft', 'sent', 'signed', 'archived', 'voided'].map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-[#6C757D]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6C757D]">No documents match the current filter.</div>
          ) : (
            <div className="divide-y">
              {filtered.map((d) => (
                <div key={d.id} className="p-4 flex items-start justify-between hover:bg-gray-50" data-testid={`crm-doc-row-${d.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={TYPE_COLORS[d.template_type] || 'bg-gray-500 text-white'}>{(d.template_type || '').toUpperCase()}</Badge>
                      <span className="font-semibold text-[#2B2D42]">{d.template_name}</span>
                      <code className="text-[10px] text-[#6C757D] bg-gray-100 px-1.5 py-0.5 rounded">{d.template_code}</code>
                      <code className="text-[10px] text-[#6C757D] bg-white border px-1.5 py-0.5 rounded">{(d.language || 'en').toUpperCase()}</code>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_STYLES[d.status] || STATUS_STYLES.draft}`}>
                        <Circle className="w-2 h-2 inline mr-1 fill-current" />{d.status}
                      </span>
                    </div>
                    <div className="text-xs text-[#6C757D] mt-1">
                      <b>Counterparty:</b> {d.counterparty_snapshot?.company_name || d.counterparty_snapshot?.name || '—'}
                      <span className="mx-2">·</span>
                      <b>Issuer:</b> {d.company_snapshot?.legal_name || d.company_snapshot?.trading_name || '—'}
                      <span className="mx-2">·</span>
                      <span>{formatDate(d.generated_at)}</span>
                    </div>
                    {d.notes && <div className="text-xs text-[#6C757D] italic mt-1">{d.notes}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={d.status}
                      onChange={(e) => updateStatus(d, e.target.value)}
                      className="text-xs px-2 py-1 border rounded bg-white"
                      data-testid={`crm-doc-status-${d.id}`}
                    >
                      <option value="draft">draft</option>
                      <option value="sent">sent</option>
                      <option value="signed">signed</option>
                      <option value="archived">archived</option>
                      <option value="voided">voided</option>
                    </select>
                    {d.status === 'draft' && (
                      <button
                        type="button"
                        onClick={() => handleEditDraft(d)}
                        className="p-2 rounded border bg-white hover:bg-orange-50 text-[#FF6B35]"
                        title="Edit draft — resume in wizard"
                        data-testid={`crm-doc-edit-${d.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <a
                      href={`${API_URL}/api/crm/generated-documents/${d.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 rounded border bg-white hover:bg-gray-100"
                      title="Download / preview PDF"
                      data-testid={`crm-doc-download-${d.id}`}
                    >
                      <Download className="w-4 h-4 text-[#023E8A]" />
                    </a>
                    <button
                      type="button"
                      onClick={() => setEmailDialog(d)}
                      className="p-2 rounded border bg-white hover:bg-[#00B4D8]/10 text-[#00B4D8]"
                      title="Send by email to counterparty"
                      data-testid={`crm-doc-email-${d.id}`}
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(d)}
                      className="p-2 rounded border bg-white hover:bg-red-50 text-red-600"
                      title="Delete document (permanent)"
                      data-testid={`crm-doc-delete-${d.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {picker.open && (
        <TemplatePicker
          onClose={() => setPicker({ open: false })}
          onSelect={(tpl) => { setPicker({ open: false }); setWizard({ open: true, template: tpl, editingDoc: null }); }}
        />
      )}

      {wizard.open && wizard.template && (
        <GeneratePDFWizard
          template={wizard.template}
          editingDoc={wizard.editingDoc}
          onClose={() => { setWizard({ open: false, template: null, editingDoc: null }); fetchDocs(); }}
        />
      )}

      {emailDialog && (
        <EmailDialog
          doc={emailDialog}
          onClose={() => setEmailDialog(null)}
          onSent={() => { setEmailDialog(null); fetchDocs(); }}
        />
      )}
    </div>
  );
};


// --------- Email dispatch dialog ---------
const EmailDialog = ({ doc, onClose, onSent }) => {
  const { toast } = useToast();
  const counterparty = doc.counterparty_snapshot || {};
  const defaultTo = counterparty.email || '';
  const docTitle = doc.template_name || doc.template_code || 'Document';

  const [form, setForm] = useState({
    to: defaultTo,
    cc: '',
    subject: `${docTitle} from Grupo BMP`,
    message: '',
  });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!form.to.trim()) {
      toast({ variant: 'destructive', title: 'Recipient required', description: 'Please add at least one address.' });
      return;
    }
    setSending(true);
    try {
      const payload = {
        to: form.to.trim(),
        subject: form.subject.trim() || undefined,
        message: form.message.trim() || undefined,
        cc: form.cc.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const r = await axios.post(`${API_URL}/api/crm/generated-documents/${doc.id}/send-email`, payload);
      toast({
        title: 'Email dispatched',
        description: `Sent to ${r.data.to} (${Math.round((r.data.attachment_size || 0) / 1024)} KB)`,
      });
      onSent && onSent();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Send failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="crm-doc-email-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#00B4D8] mb-0.5">Send Document</div>
            <h3 className="text-base font-bold">{docTitle}</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!defaultTo && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 inline-flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> Counterparty has no email on file — add one below.
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">To *</label>
            <input
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              placeholder="recipient@company.com"
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              data-testid="email-to"
            />
            {counterparty.name && (
              <div className="text-[11px] text-gray-500 mt-1">Counterparty: <b>{counterparty.name}</b></div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">CC (comma-separated)</label>
            <input
              value={form.cc}
              onChange={(e) => setForm({ ...form, cc: e.target.value })}
              placeholder="optional@cc.com, another@cc.com"
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              data-testid="email-cc"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Subject</label>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              data-testid="email-subject"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Custom message (optional)</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              placeholder="Add a personal note above the standard body…"
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              data-testid="email-message"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              The PDF will be attached automatically and a "Download" button will be included in the email.
            </p>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending}
            className="bg-[#00B4D8] hover:bg-[#0096B7] text-white"
            data-testid="email-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};


// --------- Template picker modal ---------
const TemplatePicker = ({ onClose, onSelect }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/cms/document-templates/`);
        setTemplates(r.data || []);
      } catch (_) { setTemplates([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return templates;
    const s = search.toLowerCase();
    return templates.filter((t) =>
      [t.code, t.name, t.type, t.description].filter(Boolean).some((f) => String(f).toLowerCase().includes(s))
    );
  }, [templates, search]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="template-picker">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-5 py-3 bg-gradient-to-r from-[#023E8A] to-[#0353A4] text-white">
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5" /><h3 className="font-bold">Select a template</h3></div>
          <button onClick={onClose} data-testid="template-picker-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              placeholder="Search by code, name, type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              data-testid="template-picker-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {loading && <div className="p-8 text-center text-[#6C757D]">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="p-8 text-center text-[#6C757D]">No templates match.</div>}
          <div className="divide-y">
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t)}
                className="w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center justify-between"
                data-testid={`template-picker-option-${t.code}`}
              >
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={TYPE_COLORS[t.type] || 'bg-gray-500 text-white'}>{(t.type || '').toUpperCase()}</Badge>
                    <span className="font-semibold text-[#2B2D42]">{t.name}</span>
                    <code className="text-[10px] text-[#6C757D] bg-gray-100 px-1.5 py-0.5 rounded">{t.code}</code>
                  </div>
                  {t.description && <p className="text-xs text-[#6C757D] mt-1">{t.description}</p>}
                </div>
                <span className="text-[11px] text-[#023E8A] font-medium">Select →</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) { return iso; }
}

export default GeneratedDocumentsPage;
